import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import PlayPauseButton from "./PlayPauseButton";
import NoiseGenerator from "./NoiseGenerator";
import NoiseSelectionGroup from "./NoiseSelectionGroup";
import VolumeSlider from "./VolumeSlider";
import NoiseType from "./NoiseType";

// Switches the iOS audio session from "ambient" (muted by silent switch) to
// "playback" (bypasses silent switch), then calls onReady. Must be called from
// a user gesture. onReady fires synchronously on iOS 17+ or after ~100ms on older iOS.
const switchToPlaybackSession = (() => {
  let switched = false;
  return (onReady: () => void) => {
    // iOS 17+: AudioSession API is synchronous.
    const nav = navigator as any;
    if (nav.audioSession) {
      nav.audioSession.type = 'playback';
      onReady();
      return;
    }

    if (switched) { onReady(); return; }

    // iOS < 17: play a 0.5s silent WAV through HTMLAudioElement to switch the session.
    const sr = 44100;
    const dataSize = (sr / 2) * 2; // 0.5s × 1ch × 2 bytes
    const buf = new ArrayBuffer(44 + dataSize);
    const v = new DataView(buf);
    const str = (o: number, s: string) => [...s].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)));
    str(0, 'RIFF'); v.setUint32(4, 36 + dataSize, true);
    str(8, 'WAVE');
    str(12, 'fmt '); v.setUint32(16, 16, true);
    v.setUint16(20, 1, true); v.setUint16(22, 1, true);
    v.setUint32(24, sr, true); v.setUint32(28, sr * 2, true);
    v.setUint16(32, 2, true); v.setUint16(34, 16, true);
    str(36, 'data'); v.setUint32(40, dataSize, true);

    const url = URL.createObjectURL(new Blob([buf], { type: 'audio/wav' }));
    const audio = new Audio(url);
    audio.play()
      .then(() => {
        switched = true;
        setTimeout(() => { audio.pause(); URL.revokeObjectURL(url); }, 600);
        onReady(); // session is now in playback — safe to start Web Audio
      })
      .catch(() => { URL.revokeObjectURL(url); onReady(); }); // best-effort fallback
  };
})();

const Noise = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.1);
  const [noiseType, setNoiseType] = useState(NoiseType.White);

  const noiseGeneratorRef = useRef<NoiseGenerator | null>(null);

  const onPlayButtonClick = () => {
    if (noiseGeneratorRef.current === null) {
      noiseGeneratorRef.current = new NoiseGenerator(volume, noiseType);
      // On first press, wait for the iOS audio session to switch to "playback"
      // before starting noise — avoids the silent-switch muting the first play.
      switchToPlaybackSession(() => {
        noiseGeneratorRef.current?.audioContext.resume();
        setIsPlaying(true);
      });
    } else {
      noiseGeneratorRef.current.audioContext.resume();
      setIsPlaying(prev => !prev);
    }
  }

  useEffect(() => {
    if (noiseGeneratorRef.current === null) return;

    if (isPlaying) {
      noiseGeneratorRef.current.play();
    } else {
      noiseGeneratorRef.current.stop();
    }
  }, [isPlaying]);

  useEffect(() => {
    if (noiseGeneratorRef.current === null) {
      return;
    }

    noiseGeneratorRef.current.setVolume(volume);
  }, [volume])

  useEffect(() => {
    if (noiseGeneratorRef.current === null) {
      return;
    }

    noiseGeneratorRef.current.setNoiseType(noiseType);
  }, [noiseType]);

  return <div className="fixed h-full w-full bg-[#09090b]">
    <Helmet>
      <title>Noise Generator</title>
      <meta name="description" content="A simple to use white, pink, and brown noise generator for sleep and focus." />
    </Helmet>

    <Link to="/" className="absolute top-8 left-8 text-zinc-500 hover:text-zinc-300 text-sm transition-colors duration-150">← back</Link>

    <div className="h-full flex flex-col items-center justify-center gap-12">
      <p className="text-zinc-500 text-xs tracking-widest uppercase">noise generator</p>
      <PlayPauseButton onClick={onPlayButtonClick} isPlaying={isPlaying} />
      <NoiseSelectionGroup selectedType={noiseType} onClickButton={(noiseType) => setNoiseType(noiseType)}/>
      <VolumeSlider volume={volume} onVolumeChange={(newVolume) => setVolume(newVolume)} />
    </div>
  </div>
}

export default Noise;
